import React, { useState, useEffect } from 'react';
import { MessageSquare, CheckCircle, XCircle, Clock } from 'lucide-react';
import { getIntegrationTemplates, IntegrationTemplate } from '@/actions/drip-campaigns';

interface WhatsAppTemplateSelectorProps {
  value?: number;
  onChange: (template: IntegrationTemplate | null, parameterMapping: Record<string, string>) => void;
  parameterMapping?: Record<string, string>;
}

const WhatsAppTemplateSelector: React.FC<WhatsAppTemplateSelectorProps> = ({
  value,
  onChange,
  parameterMapping = {}
}) => {
  const [templates, setTemplates] = useState<IntegrationTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<IntegrationTemplate | null>(null);
  const [localParamMapping, setLocalParamMapping] = useState<Record<string, string>>(parameterMapping);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (value && templates.length > 0) {
      const template = templates.find(t => t.id === value);
      setSelectedTemplate(template || null);
    }
  }, [value, templates]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await getIntegrationTemplates({
        channel: 'whatsapp',
        status: 'APPROVED'
      });

      if (response.success && response.data) {
        setTemplates(response.data);
      }
      // If not success (e.g. no integration), show empty state silently
    } catch (error) {
      console.error('Error loading WhatsApp templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template: IntegrationTemplate) => {
    setSelectedTemplate(template);

    // Initialize parameter mapping with empty values
    const defaultMapping: Record<string, string> = {};
    template.parameters.forEach((_param, index) => {
      const paramKey = `${index + 1}`;
      defaultMapping[paramKey] = localParamMapping[paramKey] || '';
    });

    setLocalParamMapping(defaultMapping);
    onChange(template, defaultMapping);
  };

  const handleParamChange = (paramIndex: string, leadField: string) => {
    const newMapping = { ...localParamMapping, [paramIndex]: leadField };
    setLocalParamMapping(newMapping);
    onChange(selectedTemplate, newMapping);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'PENDING':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getCategoryBadge = (category: string) => {
    const colors: Record<string, string> = {
      MARKETING: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      UTILITY: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      AUTHENTICATION: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${colors[category] || 'bg-gray-100 text-gray-800'}`}>
        {category}
      </span>
    );
  };

  const leadFieldOptions = [
    { value: '', label: '-- Select Lead Field --' },
    { value: 'name', label: 'Lead Name' },
    { value: 'phone', label: 'Phone Number' },
    { value: 'email', label: 'Email' },
    { value: 'status', label: 'Status' },
    { value: 'source', label: 'Source' },
    { value: 'sub_source', label: 'Sub Source' },
    { value: 'city', label: 'City' },
    { value: 'state', label: 'State' },
    { value: 'custom_fields.company_name', label: 'Company Name' },
    { value: 'custom_fields.product_interest', label: 'Product Interest' }
  ];

  const inputClassName = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100";

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading WhatsApp templates...</p>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center">
        <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">No WhatsApp Templates Found</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Please sync your AiSensy templates first from the Integrations page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Template Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          WhatsApp Template *
        </label>
        <select
          value={selectedTemplate?.id || ''}
          onChange={(e) => {
            const template = templates.find(t => t.id === parseInt(e.target.value));
            if (template) handleTemplateSelect(template);
          }}
          className={inputClassName}
        >
          <option value="">-- Select Template --</option>
          {templates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.element_name} ({template.language})
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Only approved WhatsApp templates are shown
        </p>
      </div>

      {/* Template Preview */}
      {selectedTemplate && (
        <div className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Template Preview
            </h4>
            <div className="flex items-center gap-2">
              {getCategoryBadge(selectedTemplate.category)}
              {getStatusIcon(selectedTemplate.status)}
            </div>
          </div>

          {/* WhatsApp-style Message Preview */}
          <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-600 max-w-md">
            {/* Header */}
            {selectedTemplate.header && (
              <div className="font-bold text-gray-900 dark:text-gray-100 mb-2">
                {selectedTemplate.header}
              </div>
            )}

            {/* Body */}
            {selectedTemplate.body && (
              <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap mb-2">
                {selectedTemplate.body.split(/(\{\{\d+\}\})/).map((part, idx) => {
                  const match = part.match(/\{\{(\d+)\}\}/);
                  if (match) {
                    const paramNum = match[1];
                    const mappedField = localParamMapping[paramNum] || `{{${paramNum}}}`;
                    return (
                      <span key={idx} className="font-semibold text-brandColor">
                        {mappedField}
                      </span>
                    );
                  }
                  return part;
                })}
              </div>
            )}

            {/* Footer */}
            {selectedTemplate.footer && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                {selectedTemplate.footer}
              </div>
            )}

            {/* Buttons */}
            {selectedTemplate.buttons && selectedTemplate.buttons.length > 0 && (
              <div className="mt-3 space-y-1">
                {selectedTemplate.buttons.map((button: any, idx: number) => (
                  <button
                    key={idx}
                    className="w-full py-2 px-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-brandColor text-sm font-medium"
                    disabled
                  >
                    {button.text}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Template Info */}
          <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
            <p><span className="font-medium">Language:</span> {selectedTemplate.language}</p>
            <p><span className="font-medium">Parameters:</span> {selectedTemplate.parameters.length}</p>
          </div>
        </div>
      )}

      {/* Parameter Mapping */}
      {selectedTemplate && selectedTemplate.parameters.length > 0 && (
        <div className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Map Template Parameters</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Map template placeholders to lead fields. Parameters will be replaced with actual lead data when sending.
          </p>

          <div className="space-y-3">
            {selectedTemplate.parameters.map((_param, index) => {
              const paramIndex = `${index + 1}`;
              return (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-24 flex-shrink-0">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Parameter {paramIndex}:
                    </span>
                  </div>
                  <select
                    value={localParamMapping[paramIndex] || ''}
                    onChange={(e) => handleParamChange(paramIndex, e.target.value)}
                    className={inputClassName}
                  >
                    {leadFieldOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              💡 <span className="font-medium">Tip:</span> Unmapped parameters will be sent as empty values. Make sure to map all required parameters.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplateSelector;
